// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadCatalogPageByPath from "@insite/client-framework/Store/Data/CatalogPages/Handlers/LoadCatalogPageByPath";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showImages = "showImages",
    // XNG-Change
    showSubLevel = "showSubLevel",
}

interface Props extends WidgetProps, HasCategoryContext {}

export interface CategoryDetailSubCategoriesStyles {
    container?: GridContainerProps;
    subCategoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    subCategoryImageItem?: GridItemProps;
    subCategoryImageClickable?: ClickablePresentationProps;
    subCategoryImage?: LazyImageProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    // XNG-Change
    subCategoryNameLinkInner?: LinkPresentationProps;
    imageTitleWrapper?: InjectableCss;
    subCategoryListWrapper?: InjectableCss;
    subCategoryListViewAll?: InjectableCss;
    subCategoryViewAllLink?: LinkPresentationProps;
}

export const categoryDetailSubCategoriesStyles: CategoryDetailSubCategoriesStyles = {
    container: {
        gap: 0,
    },
    subCategoryItem: {
        width: [12, 12, 4, 4, 2],
        css: css`
            padding-bottom: 30px;
        `,
    },
    innerContainer: {
        gap: 0,
    },
    subCategoryImageItem: {
        width: [2, 2, 12, 12, 12],
        css: css`
            width: 100%;
            padding: 0 8px 8px 8px;
        `,
    },
    subCategoryImageClickable: {
        css: css`
            width: 100%;
        `,
    },
    subCategoryImage: {
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    subCategoryNameLinkItem: {
        width: [10, 10, 12, 12, 12],
        css: css`
            padding-bottom: 8px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        justify-content: center;
                    `,
                    css`
                        justify-content: center;
                    `,
                    css`
                        justify-content: center;
                    `,
                ])}
        `,
    },
    subCategoryNameLink: {
        css: css`
            width: 100%;
        `,
        color: "text.main",
        typographyProps: {
            weight: "bold",
            css: css`
                width: 100%;
                text-align: center;
                word-wrap: break-word;
            `,
        },
    },
};

const styles = categoryDetailSubCategoriesStyles;

const CategoryDetailSubCategories = ({ category, fields: { showImages, showSubLevel } }: Props) => {
    if (!category || !category.subCategoryIds || category.subCategoryIds.length === 0) {
        return null;
    }

    return (
        <GridContainer {...styles.container}>
            {category.subCategoryIds.map(subCategoryId => (
                <GridItem key={subCategoryId} {...styles.subCategoryItem}>
                    <SubCategoryLink categoryId={subCategoryId} showImages={showImages} showSubLevel={showSubLevel} />
                </GridItem>
            ))}
        </GridContainer>
    );
};

// XNG-Change: structure
const SubCategoryLinkView = ({
    category,
    showImages,
    showSubLevel,
    loadCatalogPageByPath,
}: ReturnType<typeof mapStateToProps> & { showImages: boolean; showSubLevel: boolean } & ResolveThunks<
        typeof mapDispatchToProps
    >) => {
    if (!category) {
        return null;
    }

    React.useEffect(() => {
        if (!category.subCategoryIds?.length) {
            loadCatalogPageByPath({ path: category.path });
        }
    }, []);

    return (
        <StyledWrapper {...styles.innerContainer}>
            <StyledWrapper {...styles.imageTitleWrapper}>
                {showImages && (
                    <StyledWrapper {...styles.subCategoryImageItem}>
                        {category.smallImagePath && (
                            <Clickable
                                // xng-change
                                href={
                                    category.subCategoryIds?.length
                                        ? category.path
                                        : `${category.path}?stockedItemsOnly=true`
                                }
                                {...styles.subCategoryImageClickable}
                                aria-label="category image"
                            >
                                <LazyImage
                                    src={category.smallImagePath}
                                    altText={category.imageAltText}
                                    {...styles.subCategoryImage}
                                />
                            </Clickable>
                        )}
                    </StyledWrapper>
                )}
                <Link
                    // xng-change
                    href={category.subCategoryIds?.length ? category.path : `${category.path}?stockedItemsOnly=true`}
                    {...styles.subCategoryNameLink}
                    data-test-selector={`categoryDetailsSubCategoriesLink_${category.id}`}
                >
                    {category.shortDescription}
                </Link>
            </StyledWrapper>
            {showSubLevel && (
                <>
                    <StyledWrapper {...styles.subCategoryListWrapper}>
                        {category.subCategoryIds?.slice(0, 4).map(subCategoryId => (
                            <SubCategoryLinkInner categoryId={subCategoryId} key={subCategoryId} />
                        ))}
                    </StyledWrapper>
                    {!!category.subCategoryIds?.length && category.subCategoryIds.length > 4 && (
                        <StyledWrapper {...styles.subCategoryListViewAll}>
                            <Link
                                // xng-change
                                href={
                                    category.subCategoryIds?.length
                                        ? category.path
                                        : `${category.path}?stockedItemsOnly=true`
                                }
                                {...styles.subCategoryViewAllLink}
                                data-test-selector={`categoryDetailsSubCategoriesViewAll_${category.id}`}
                            >
                                {translate("View More")}
                            </Link>
                        </StyledWrapper>
                    )}
                </>
            )}
        </StyledWrapper>
    );
};

// xng-change
const mapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
});

const mapDispatchToProps = {
    loadCatalogPageByPath,
};

const SubCategoryLink = connect(mapStateToProps, mapDispatchToProps)(SubCategoryLinkView);

// XNG-Change
const SubCategoryLinkInnerView = ({ category }: ReturnType<typeof linkInnerMapStateToProps>) => {
    if (!category) {
        return null;
    }

    return (
        <Link
            // XNG-Changes
            href={category.subCategoryIds?.length ? category.path : `${category.path}?stockedItemsOnly=true`}
            {...styles.subCategoryNameLinkInner}
        >
            {category.shortDescription}
        </Link>
    );
};

const linkInnerMapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
});

const SubCategoryLinkInner = connect(linkInnerMapStateToProps)(SubCategoryLinkInnerView);

const widgetModule: WidgetModule = {
    component: withCategory(CategoryDetailSubCategories),
    definition: {
        group: "Category Details",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            // XNG-Change
            {
                name: fields.showSubLevel,
                displayName: "Show SubLevel",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        allowedContexts: ["CategoryDetailsPage"],
    },
};

export default widgetModule;
